import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, ArrowUpRight, MessageSquareQuote, PenLine, ClipboardList,
         Compass, Gem, Trophy, CalendarDays, Sparkles, Orbit, Lock, Clock,
         Settings2 } from 'lucide-react';
import { MoodPicker } from './MoodPicker';
import { MyWork } from './MyWork';
import { ReflectionJournal } from './ReflectionJournal';
import { MyCalendar } from './MyCalendar';
import { Milestones } from './Milestones';
import { Insights } from './Insights';
import { ValuesSpace } from './ValuesSpace';
import CompanionFull from './CompanionFull';
import { RitualBuilder } from './rituals/RitualBuilder';
import { CompanionSettings } from './CompanionSettings';
import { hasCompanion } from './access';
import { UnlockCountdown } from '@/components/UnlockCountdown';
import { cssUrl } from '@/lib/cssUrl';

/** What the member is waiting on, or what's open for them right now. */
const useNextUnlock = () => {
  const [next, setNext] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/drip/next-unlock`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((r) => setNext(r.data))
      .catch(() => {});
  }, []);
  return next;
};

// Cards read the member's own settings: hidden ones vanish, the rest take their chosen order,
// and the wide ones keep a full row at every size.
export const SettingsCtx = createContext(null);

const Card = ({ to, title, lead, note, icon: Icon, badge, accent, children, testid }) => {
  const settings = useContext(SettingsCtx);
  const key = (testid || '').replace(/^card-/, '');
  if (settings?.hidden_cards?.includes(key)) return null;
  const wide = settings?.cards?.find((c) => c.key === key)?.wide;
  const order = settings?.card_order?.length ? settings.card_order.indexOf(key) : -1;

  return (
  <Link
    to={to}
    style={order >= 0 ? { order } : undefined}
    className={`group relative flex flex-col justify-between overflow-hidden rounded-2xl border
                ${accent} ${wide ? 'col-span-2' : ''} backdrop-blur-md
                p-4 sm:p-6 min-h-[150px] sm:min-h-[190px] transition-all duration-300
                hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/40`}
    data-testid={testid}
  >
    <div className="flex items-start justify-between gap-3">
      <span className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-white/60" />
        <h2 className="text-base md:text-lg font-semibold text-white">{title}</h2>
      </span>
      {badge}
    </div>
    <div className="mt-4 space-y-1">
      {lead && <p className="text-2xl font-semibold text-white leading-tight">{lead}</p>}
      {note && <p className="text-sm text-white/55">{note}</p>}
      {children}
    </div>
    <span className="mt-4 inline-flex items-center gap-1 text-xs text-white/40
                     group-hover:text-white/80 transition-colors duration-300">
      Open <ArrowUpRight className="w-3 h-3" />
    </span>
  </Link>
  );
};

const NewBadge = ({ count, label }) => (
  count > 0 ? (
    <span className="rounded-full border border-amber-300/40 bg-amber-300/15 px-2.5 py-0.5
                     text-[10px] text-amber-100 whitespace-nowrap">
      {count} {label}
    </span>
  ) : null
);

const Dashboard = ({ data, onReload, settings, onOpenSettings }) => {
  const nextUnlock = useNextUnlock();
  return (
  <SettingsCtx.Provider value={settings}>
  <div className="space-y-8 pb-16" data-testid="companion-space">
    <header className="space-y-3" data-testid="companion-space-hero">
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
          {settings?.space_name
            || (data.name ? `${data.name}'s Consciousness Companion`
                          : 'Your Consciousness Companion')}
        </h1>
        <button onClick={onOpenSettings}
                className="shrink-0 mt-2 rounded-full border border-white/15 bg-white/[0.04] p-2.5
                           text-white/60 hover:text-white hover:rotate-90 hover:border-white/35
                           transition-all duration-300"
                title="Make this space yours"
                data-testid="companion-settings-btn">
          <Settings2 className="w-4 h-4" />
        </button>
      </div>
      <p className="text-base md:text-lg text-white/55 max-w-2xl">
        {settings?.opening_line
          || 'Your work, your words and your journey — gathered in one place that belongs to you.'}
      </p>
    </header>

    {data.quote && (
      <figure className="rounded-2xl border border-violet-300/25 bg-violet-400/[0.06] p-6"
              data-testid="companion-space-quote">
        <MessageSquareQuote className="w-4 h-4 text-violet-300/70 mb-3" />
        <blockquote className="text-base md:text-lg text-white/90 leading-relaxed max-w-3xl">
          {data.quote.text}
        </blockquote>
        <figcaption className="text-xs text-white/40 mt-3">{data.quote.source}</figcaption>
      </figure>
    )}

    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 space-y-3"
             data-testid="companion-space-mood">
      <div>
        <h2 className="text-base md:text-lg font-semibold text-white">Where are you right now?</h2>
        <p className="text-sm text-white/45">
          {data.mood?.logged_today
            ? `${data.mood.logged_today} reading${data.mood.logged_today === 1 ? '' : 's'} today · latest ${data.mood.latest_label}`
            : 'Tap where you are. As many times a day as you like.'}
        </p>
      </div>
      <MoodPicker
        moods={data.moods}
        latest={data.mood?.logged_today ? data.mood.latest : null}
        loggedToday={data.mood?.logged_today}
        onLogged={onReload}
      />
    </section>

    <div className="grid gap-3 sm:gap-5 grid-cols-2 md:grid-cols-3">
      {nextUnlock && nextUnlock.status === 'waiting' && (
        <Card
          to="/dashboard/modules"
          title="Opening next"
          icon={Clock}
          accent="border-amber-300/25 bg-amber-400/[0.06] hover:border-amber-300/50"
          lead={nextUnlock.title}
          note={`Module ${nextUnlock.module_order} · ${nextUnlock.module_title}`}
          testid="card-opening-next"
        >
          <div className="pt-1">
            <UnlockCountdown unlockDate={nextUnlock.unlock_date} testId="companion-next-countdown" />
          </div>
        </Card>
      )}

      {nextUnlock && nextUnlock.status === 'in_progress' && (
        <Card
          to={`/dashboard/modules/${nextUnlock.module_id}`}
          title="Open now"
          icon={Clock}
          accent="border-emerald-300/25 bg-emerald-400/[0.06] hover:border-emerald-300/50"
          lead={nextUnlock.title}
          note={`Module ${nextUnlock.module_order} · ${nextUnlock.module_title}`}
          testid="card-opening-next"
        />
      )}

      {nextUnlock && nextUnlock.status === 'complete_module' && (
        <Card
          to={`/dashboard/modules/${nextUnlock.module_id}`}
          title="Ready to close out"
          icon={Clock}
          accent="border-emerald-300/25 bg-emerald-400/[0.06] hover:border-emerald-300/50"
          lead={nextUnlock.module_title}
          note={`Every folder is done — mark Module ${nextUnlock.module_order} complete`}
          testid="card-opening-next"
        />
      )}

      <Card
          to="/dashboard/companion/work"
          title="My work"
          icon={ClipboardList}
          accent="border-cyan-300/25 bg-cyan-400/[0.06] hover:border-cyan-300/50"
          badge={<NewBadge count={data.work.feedback_unread} label="new" />}
          lead={`${data.work.handed_in} handed in`}
          note={[
            data.work.in_progress ? `${data.work.in_progress} in progress` : null,
            data.work.to_do ? `${data.work.to_do} waiting on you` : null,
          ].filter(Boolean).join(' · ') || 'Everything you complete is kept here'}
          testid="card-my-work"
        >
          {data.work.next_up && (
            <p className="text-xs text-white/45 pt-1">
              Next up: <span className="text-white/70">{data.work.next_up.title}</span>
            </p>
          )}
        </Card>

      <Card
        to="/dashboard/companion/journal"
        title="Reflection journal"
        icon={PenLine}
        accent="border-teal-300/25 bg-teal-400/[0.06] hover:border-teal-300/50"
        lead={data.journal.wrote_today ? 'You wrote today' : 'Nothing written today'}
        note={`${data.journal.entries} entr${data.journal.entries === 1 ? 'y' : 'ies'} · ${data.journal.days_this_month} day${data.journal.days_this_month === 1 ? '' : 's'} this month`}
        testid="card-journal"
      >
        {data.journal.last_entry_excerpt && (
          <p className="text-xs text-white/40 line-clamp-2 pt-1">
            “{data.journal.last_entry_excerpt}”
          </p>
        )}
      </Card>

      <Card
        to="/dashboard/companion/journey"
        title="Consciousness Progression"
        icon={Compass}
        accent="border-violet-300/25 bg-violet-400/[0.06] hover:border-violet-300/50"
        lead={data.assessments
          ? `${data.assessments} assessment${data.assessments === 1 ? '' : 's'}`
          : 'Your first reading awaits'}
        note="Your dimensional arc across the program"
        testid="card-journey"
      />

      <Card
        to="/dashboard/companion/calendar"
        title="My calendar"
        icon={CalendarDays}
        accent="border-sky-300/25 bg-sky-400/[0.06] hover:border-sky-300/50"
        lead={data.calendar?.next
          ? new Date(data.calendar.next.at).toLocaleDateString(undefined,
              { weekday: 'short', month: 'short', day: 'numeric' })
          : 'Nothing booked'}
        note={data.calendar?.next
          ? `${data.calendar.next.title}${data.calendar.next.with ? ` · with ${data.calendar.next.with}` : ''}`
          : 'Sessions, live gatherings and community dates'}
        testid="card-calendar"
      >
        {data.calendar?.upcoming > 1 && (
          <p className="text-xs text-white/40 pt-1">
            {data.calendar.upcoming} things ahead of you
          </p>
        )}
      </Card>

      {data.rituals?.unlocked ? (
        <Card
          to="/dashboard/companion/rituals"
          title="Sacred Habit Stack"
          icon={Orbit}
          accent="border-amber-300/25 bg-amber-300/[0.06] hover:border-amber-300/50"
          lead={data.rituals.ritual_count
            ? `${data.rituals.percent}% of this week`
            : 'Build your seven days'}
          note={data.rituals.ritual_count
            ? `${data.rituals.ritual_count} ritual${data.rituals.ritual_count === 1 ? '' : 's'} · ${data.rituals.current_streak} day run · ${data.rituals.active_days} days practised`
            : 'Premade rituals from the program, or write your own'}
          testid="card-rituals"
        >
          <p className="text-xs text-white/40 pt-1">Morning · Midday · Evening</p>
        </Card>
      ) : (
        <div
          className="relative flex flex-col justify-between overflow-hidden rounded-2xl border
                     border-white/10 bg-white/[0.02] p-6 min-h-[190px]"
          data-testid="card-rituals-locked"
        >
          <span className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-white/40" />
            <h2 className="text-base md:text-lg font-semibold text-white/70">
              Sacred Habit Stack
            </h2>
          </span>
          <div className="mt-4 space-y-1">
            <p className="text-2xl font-semibold text-white/60 leading-tight">
              Unlocks during Module 10
            </p>
            <p className="text-sm text-white/40">
              {data.rituals?.gate_section
                ? `Finish “${data.rituals.gate_section}” and your ritual builder opens here.`
                : 'Your seven-day ritual system, with a mandala that fills as you practise.'}
            </p>
          </div>
          <span className="mt-4 text-xs text-white/30">Earned, not given</span>
        </div>
      )}

      <Card
        to="/dashboard/companion/milestones"
        title="My milestones"
        icon={Trophy}
        accent="border-amber-300/25 bg-amber-300/[0.06] hover:border-amber-300/50"
        lead={`${data.milestones?.earned || 0} reached`}
        note={data.milestones?.latest
          ? `Latest: ${data.milestones.latest.title}`
          : 'Every marker you pass on the program'}
        testid="card-milestones"
      >
        <p className="text-xs text-white/40 pt-1">Yours alone — nobody else sees these</p>
      </Card>

      <Card
        to="/dashboard/companion/insights"
        title="My insights"
        icon={Sparkles}
        accent="border-emerald-300/25 bg-emerald-400/[0.06] hover:border-emerald-300/50"
        lead={data.insights?.weeks
          ? `${data.insights.weeks} week${data.insights.weeks === 1 ? '' : 's'} in`
          : 'Your journey, read back'}
        note={data.insights?.notice || 'What your own numbers quietly say'}
        testid="card-insights"
      >
        <p className="text-xs text-white/40 pt-1">
          Includes your printable Journey Keepsake
        </p>
      </Card>

      <Card
        to="/dashboard/companion/values"
        title="Values, beliefs & intentions"
        icon={Gem}
        accent="border-amber-300/25 bg-amber-300/[0.06] hover:border-amber-300/50 md:col-span-2"
        lead={data.values?.length ? data.values.join(' · ') : 'Your three values'}
        note={data.checkins?.completed
          ? `${data.checkins.completed} check-in${data.checkins.completed === 1 ? '' : 's'} completed`
          : 'Your check-ins gather here as you go'}
        testid="card-values"
      >
        {data.checkins?.next && (
          <p className="text-xs text-white/45 pt-1">
            Next: <span className="text-white/70">{data.checkins.next.title}</span>
          </p>
        )}
      </Card>
    </div>

    <p className="text-sm text-white/35" data-testid="companion-space-next">
      Everything here is yours. Nothing in this space is shown to anyone unless you choose to
      share it.
    </p>
  </div>
  </SettingsCtx.Provider>
  );
};

const FullScreen = ({ title, subtitle, children, testid, wide, background, scrim = 55,
                     theme = 'dark' }) => {
  const navigate = useNavigate();
  return (
    <div
      className="fixed inset-0 z-[70] overflow-y-auto bg-[#070a16] bg-cover bg-center bg-fixed"
      style={background
        ? { backgroundImage: cssUrl(background) }
        : { backgroundImage: 'radial-gradient(120% 60% at 80% -10%, rgba(56,189,248,0.10), transparent 60%)' }}
      data-testid={testid}
    >
      {background && (
        <div className="fixed inset-0 pointer-events-none"
             style={{ background: theme === 'soft'
               ? `linear-gradient(to bottom, rgba(24,20,38,${0.3 + scrim / 400}), rgba(40,34,58,${0.25 + scrim / 300}))`
               : `linear-gradient(to bottom, rgba(7,10,22,${0.55 + scrim / 250}), rgba(7,10,22,${0.35 + scrim / 140}))` }} />
      )}
      <div className="relative sticky top-0 z-10 border-b border-white/10 bg-[#070a16]/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-5 py-4">
          <button
            onClick={() => navigate('/dashboard/companion')}
            className="inline-flex items-center gap-2 rounded-full border border-white/10
                       bg-white/[0.04] px-4 py-1.5 text-xs text-white/70 hover:text-white
                       hover:bg-white/10 transition-colors duration-200"
            data-testid="companion-view-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Companion
          </button>
          <div className="min-w-0">
            <h1 className="text-base md:text-lg font-semibold text-white truncate">{title}</h1>
            {subtitle && <p className="text-xs text-white/40 truncate">{subtitle}</p>}
          </div>
        </div>
      </div>
      <div className={`relative ${wide ? 'max-w-7xl' : 'max-w-4xl'} mx-auto px-5 py-8`}>
        {children}
      </div>
    </div>
  );
};

const Journey = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/assessments/my/companion`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-sm text-white/50">Opening your journey…</p>;
  return <CompanionFull data={data} onReload={load} />;
};

/** The member's own space: cards that open full-screen, each on its own URL. */
export const CompanionSpace = () => {
  const { view } = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [locked, setLocked] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/companion/overview`)
      .then((r) => setData(r.data))
      .catch((e) => (e.response?.status === 403 ? setLocked(true) : setFailed(true)));
  }, []);

  // `view` is in here on purpose: the dashboard stays mounted behind a full-screen card,
  // so returning from one must pull fresh counts rather than show the old snapshot.
  useEffect(() => { load(); }, [load, view]);

  useEffect(() => {
    axios.get(`${API}/companion/settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const veil = (scrim = 55, theme = 'dark') => (theme === 'soft'
    ? `linear-gradient(to bottom, rgba(24,20,38,${0.25 + scrim / 400}), rgba(40,34,58,${0.2 + scrim / 300}))`
    : `linear-gradient(to bottom, rgba(7,10,22,${0.5 + scrim / 250}), rgba(7,10,22,${0.3 + scrim / 140}))`);

  const pageBackground = settings
    ? (settings.page_background_urls?.[view || 'dashboard'] || settings.background_url || '')
    : '';
  const tones = settings?.tones || [];
  const paletteVars = tones.length
    ? { '--mandala-1': tones[0], '--mandala-2': tones[1], '--mandala-3': tones[2] }
    : {};

  if (locked || !hasCompanion(JSON.parse(localStorage.getItem('user') || 'null'))) {
    // Carry where they were, so the tier prices open on arrival and they can come straight back.
    const back = `${location.pathname}${location.search}`;
    const label = view === 'journal' ? 'your journal' : 'your Companion';
    const upgradeHref = `/dashboard/membership?upgrade=silver`
      + `&from=${encodeURIComponent(back)}&from_label=${encodeURIComponent(label)}`;
    return (
      <div className="max-w-2xl mx-auto py-24 text-center space-y-5"
           data-testid="companion-locked">
        <Gem className="w-8 h-8 text-cyan-300/70 mx-auto" />
        <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
          Your Consciousness Companion
        </h1>
        <p className="text-base md:text-lg text-white/60">
          A private place for your journal, your work and the milestones you pass on the way.
          It comes with Silver membership and above.
        </p>
        <Link to={upgradeHref}
              className="inline-flex items-center gap-2 rounded-full bg-cyan-600 px-6 py-3
                         text-sm font-medium text-white hover:bg-cyan-500
                         transition-colors duration-200"
              data-testid="companion-locked-upgrade">
          See what Silver includes <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const shell = failed ? (
    <p className="text-center py-20 text-white/60" data-testid="companion-space-error">
      Your Companion could not be loaded just now.
    </p>
  ) : data ? (
    <Dashboard data={data} onReload={load} settings={settings}
               onOpenSettings={() => setShowSettings(true)} />
  ) : (
    <p className="text-center py-20 text-white/60">Opening your Companion…</p>
  );

  return (
    <div className={settings?.theme === 'soft' ? 'companion-soft' : undefined}
         style={paletteVars}>
      {pageBackground && !view && (
        <>
          <div className="fixed inset-0 -z-10 bg-cover bg-center bg-fixed"
               style={{ backgroundImage: cssUrl(pageBackground) }}
               data-testid="companion-background" />
          <div className="fixed inset-0 -z-10 pointer-events-none"
               style={{ background: veil(settings?.scrim, settings?.theme) }} />
        </>
      )}
      <div className="max-w-6xl mx-auto">
      {shell}
      {view === 'work' && (
        <FullScreen title="My work" subtitle="Everything you have handed in, and everything said back"
                    background={pageBackground} scrim={settings?.scrim} theme={settings?.theme}
                    testid="companion-view-work">
          <MyWork />
        </FullScreen>
      )}
      {view === 'journal' && (
        <FullScreen title="Reflection journal" subtitle="Yours, private unless you choose otherwise"
                    background={pageBackground} scrim={settings?.scrim} theme={settings?.theme}
                    testid="companion-view-journal">
          <ReflectionJournal />
        </FullScreen>
      )}
      {view === 'journey' && (
        <FullScreen title="Consciousness Progression" subtitle="Your dimensional arc across the program"
                    background={pageBackground} scrim={settings?.scrim} theme={settings?.theme}
                    testid="companion-view-journey">
          <Journey />
        </FullScreen>
      )}
      {view === 'calendar' && (
        <FullScreen title="My calendar" subtitle="Sessions, live gatherings and community dates"
                    background={pageBackground} scrim={settings?.scrim} theme={settings?.theme}
                    testid="companion-view-calendar">
          <MyCalendar />
        </FullScreen>
      )}
      {view === 'milestones' && (
        <FullScreen title="My milestones" subtitle="Yours alone — nobody else sees this page"
                    background={pageBackground} scrim={settings?.scrim} theme={settings?.theme}
                    testid="companion-view-milestones">
          <Milestones />
        </FullScreen>
      )}
      {view === 'insights' && (
        <FullScreen title="My insights" subtitle="What your own journey quietly says — and your keepsake"
                    background={pageBackground} scrim={settings?.scrim} theme={settings?.theme}
                    testid="companion-view-insights">
          <Insights />
        </FullScreen>
      )}
      {view === 'rituals' && (
        <FullScreen title="Sacred Habit Stack"
                    subtitle="Seven days, three windows, one mandala"
                    background={pageBackground} scrim={settings?.scrim} theme={settings?.theme}
                    testid="companion-view-rituals" wide>
          <RitualBuilder />
        </FullScreen>
      )}
      {view === 'values' && (
        <FullScreen title="Values, beliefs & intentions"
                    subtitle="Your three values, and every check-in since"
                    background={pageBackground} scrim={settings?.scrim} theme={settings?.theme}
                    testid="companion-view-values">
          <ValuesSpace />
        </FullScreen>
      )}
      </div>
      {showSettings && settings && (
        <CompanionSettings settings={settings} onSaved={setSettings}
                           onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
};

export default CompanionSpace;
